import { translate } from "../localization";
import API from "./API";

const thumbnailBlobUrlCache = new Map();
const thumbnailRequestCache = new Map();
const DEFAULT_THUMBNAIL_SIZE = "small";

const HYDRATED_ENVIRONMENT_FIELDS = [
  "backgroundAudioUrl",
  "elementJsons",
  "environmentData",
  "metadata",
  "pk",
  "sk",
  "skyboxUrl",
  "success",
  "thumbnailUrl",
];

const getThumbnailCacheKey = (id, size) => `${id}:${size}`;

const getThumbnailSize = (options) =>
  options?.size === "large" ? "large" : DEFAULT_THUMBNAIL_SIZE;

export const invalidateThumbnailCache = (id) => {
  if (!id) return;

  const cacheKeyPrefix = `${id}:`;

  [...thumbnailBlobUrlCache.entries()].forEach(([cacheKey, blobUrl]) => {
    if (!cacheKey.startsWith(cacheKeyPrefix)) return;

    if (blobUrl && typeof URL?.revokeObjectURL === "function") {
      URL.revokeObjectURL(blobUrl);
    }

    thumbnailBlobUrlCache.delete(cacheKey);
  });

  [...thumbnailRequestCache.keys()].forEach((cacheKey) => {
    if (cacheKey.startsWith(cacheKeyPrefix)) {
      thumbnailRequestCache.delete(cacheKey);
    }
  });
};

const getPresignedThumbnailUrl = async (id, size) => {
  const sizeQuery = size === "large" ? "?size=large" : "";
  const response = await API.get(`thumbnails/${id}${sizeQuery}`);
  return typeof response?.data?.url === "string" ? response.data.url.trim() : "";
};

const cacheThumbnailAsset = async (id, cacheKey, sourceUrl) => {
  if (!sourceUrl) {
    return "";
  }

  if (typeof fetch !== "function" || typeof URL?.createObjectURL !== "function") {
    return sourceUrl;
  }

  try {
    const assetResponse = await fetch(sourceUrl, { credentials: "omit" });
    if (!assetResponse.ok) {
      throw new Error(translate("Thumbnail asset request failed with status {value0}", {
  value0: assetResponse.status
}));
    }

    const thumbnailBlob = await assetResponse.blob();
    const blobUrl = URL.createObjectURL(thumbnailBlob);
    thumbnailBlobUrlCache.set(cacheKey, blobUrl);
    return blobUrl;
  } catch (error) {
    console.warn(
      `Unable to cache thumbnail asset for ${id}; using presigned URL fallback.`,
      error
    );
    return sourceUrl;
  }
};

export const getItem = (contentType, itemId, json) => {
  return API.get(`${contentType}/${itemId}`, { requiresAuth: true }).then((response) => response.data);
};

export const getThumbnail = async (id, options = {}) => {
  if (!id) return "";

  const size = getThumbnailSize(options);
  const cacheKey = getThumbnailCacheKey(id, size);

  if (thumbnailBlobUrlCache.has(cacheKey)) {
    return thumbnailBlobUrlCache.get(cacheKey);
  }

  if (thumbnailRequestCache.has(cacheKey)) {
    return thumbnailRequestCache.get(cacheKey);
  }

  const thumbnailRequest = (async () => {
    try {
      const presignedUrl = await getPresignedThumbnailUrl(id, size);
      return await cacheThumbnailAsset(id, cacheKey, presignedUrl);
    } catch (error) {
      console.error(`Error fetching thumbnail for ${id}:`, error);
      return "";
    } finally {
      thumbnailRequestCache.delete(cacheKey);
    }
  })();

  thumbnailRequestCache.set(cacheKey, thumbnailRequest);
  return thumbnailRequest;
};

export const uploadThumbnail = async (id, image) => {
  if (!id) {
    throw new Error(translate("A thumbnail id is required."));
  }

  if (!image) {
    throw new Error(translate("Thumbnail image data is required."));
  }

  const response = await API.post(
    "thumbnails",
    { id, image },
    { requiresAuth: true }
  );

  invalidateThumbnailCache(id);
  return response?.data;
};

export const getEnvironmentAuthoringPayload = (environment = {}) => {
  const authoringEnvironment = { ...environment };

  HYDRATED_ENVIRONMENT_FIELDS.forEach((field) => {
    delete authoringEnvironment[field];
  });

  return authoringEnvironment;
};

export const uploadEnvironmentBackgroundAudio = async (environmentId, file) => {
  if (!environmentId) {
    throw new Error(translate("An environment id is required."));
  }

  if (!file) {
    throw new Error(translate("An MP3 file is required."));
  }

  const response = await API.post(
    `environments/${encodeURIComponent(environmentId)}/background-audio`,
    {},
    { requiresAuth: true }
  );
  const uploadDetails = response?.data?.data;
  const uploadUrl = uploadDetails?.uploadUrl;
  const contentType = uploadDetails?.contentType || "audio/mpeg";

  if (!uploadUrl) {
    throw new Error(translate("The audio upload URL was missing from the server response."));
  }

  const uploadResponse = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": contentType,
    },
  });

  if (!uploadResponse.ok) {
    throw new Error(
      translate("Background audio upload failed with status {value0}.", {
  value0: uploadResponse.status
})
    );
  }

  return uploadDetails;
};

export const clearThumbnailCache = () => {
  thumbnailBlobUrlCache.forEach((blobUrl) => {
    if (blobUrl && typeof URL?.revokeObjectURL === "function") {
      URL.revokeObjectURL(blobUrl);
    }
  });
  thumbnailBlobUrlCache.clear();
  thumbnailRequestCache.clear();
};
